import { useState, useEffect, useCallback, useMemo } from 'react';
import { addMonths, subMonths, isBefore, isAfter } from 'date-fns';
import { createEventsByDateMap } from '../utils';
import { Event } from '../types';

export const useCalendarState = (
    selectedDate: Date,
    startDate: Date | undefined,
    endDate: Date | undefined,
    minDate: Date,
    maxDate: Date,
    events: Event[] = []
) => {
    const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
    const [showCalendar, setShowCalendar] = useState(false);
    const [rangeStart, setRangeStart] = useState<Date | null>(startDate || null);
    const [rangeEnd, setRangeEnd] = useState<Date | null>(endDate || null);
    const [localSelectedDate, setLocalSelectedDate] = useState<Date>(selectedDate);
    const [animationState, setAnimationState] = useState('');
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [hasProcessedEvents, setHasProcessedEvents] = useState(false);

    // Update internal state when props change
    useEffect(() => {
        setLocalSelectedDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (startDate) setRangeStart(startDate);
    }, [startDate]);

    useEffect(() => {
        if (endDate) setRangeEnd(endDate);
    }, [endDate]);

    // Process events when they change
    useEffect(() => {
        if (events && events.length > 0) {
            setHasProcessedEvents(true);
        }
    }, [events]);

    // Animation effect for calendar opening/closing
    useEffect(() => {
        if (showCalendar) {
            setAnimationState('entering');
            const timer = setTimeout(() => {
                setAnimationState('entered');
            }, 150);
            return () => clearTimeout(timer);
        } else if (animationState !== '') {
            setAnimationState('exiting');
            const timer = setTimeout(() => {
                setAnimationState('');
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [showCalendar, animationState]);

    const nextMonth = useCallback(() => {
        const nextMonthDate = addMonths(currentMonth, 1);
        if (!isAfter(nextMonthDate, maxDate)) {
            setCurrentMonth(nextMonthDate);
        }
    }, [currentMonth, maxDate]);

    const prevMonth = useCallback(() => {
        const prevMonthDate = subMonths(currentMonth, 1);
        if (!isBefore(prevMonthDate, minDate)) {
            setCurrentMonth(prevMonthDate);
        }
    }, [currentMonth, minDate]);

    const openCalendar = useCallback(() => {
        setShowCalendar(true);
    }, []);

    const closeCalendar = useCallback(() => {
        setAnimationState('exiting');
        setTimeout(() => {
            setShowCalendar(false);
            setAnimationState('');
        }, 150);
    }, []);

    // Add toggleCalendar function
    const toggleCalendar = useCallback(() => {
        if (showCalendar) {
            closeCalendar();
        } else {
            openCalendar();
        }
    }, [showCalendar, closeCalendar, openCalendar]);

    // Optimize event lookup with memoization
    const eventsByDate = useMemo(() => createEventsByDateMap(events), [events]);

    return {
        currentMonth,
        setCurrentMonth,
        showCalendar,
        setShowCalendar,
        rangeStart,
        setRangeStart,
        rangeEnd,
        setRangeEnd,
        localSelectedDate,
        setLocalSelectedDate,
        animationState,
        setAnimationState,
        hoverDate,
        setHoverDate,
        hasProcessedEvents,
        nextMonth,
        prevMonth,
        openCalendar,
        closeCalendar,
        toggleCalendar,
        eventsByDate
    };
};